import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { axiosInstance } from '../services/axios-instance';
import { EventsService } from '../services/event-service/service';
import { errorMessage, parseAxiosError } from '../utils';
import { Event, EventList, Biomarker } from '../services/event-service/types';
import { PaginateOptions, StateLoading } from '../services/types';

interface EventState {
  events: Event[];
  nextPage?: number;
  prevPage?: number;
  currentEvent: Event | null;
  currentPage: number;
  totalPages: number;
  serverError: string;
  loadingEvents: boolean;
  count: number;
  totalCount: number;
  updateEvents: Record<number, StateLoading<Event>>;
  deleteEventStatus: StateLoading<number>;
  getCodes: () => void;
  biomarkers: any[];
}

interface EventAction {
  deleteEvent: (params: { id: number }) => Promise<void>;
  listEvents: { (options: PaginateOptions): Promise<EventList> };
  clearCurrentEvent: { (): void };
  getDetailedEvent: { (id: number): Promise<Event> };
  updateEvent: (id: Number, event: Partial<Event>) => Promise<void>;
  createEvent: (event: Partial<Event>) => Promise<number>;
  downloadCsv: { (eventId: number|undefined): Promise<void> };
}

const initialState = {
  events: [] as Event[],
  biomarkers: [] as Biomarker[],
  nextPage: undefined,
  prevPage: undefined,
  currentEvent: null,
  currentPage: 1,
  totalPages: 1,
  count: 0,
  totalCount: 0,
  loadingEvents: false,
  serverError: '',
  updateEvents: {},
  deleteEventStatus: {},
};

const eventService = new EventsService(axiosInstance);

export const useEventStore = create<EventState & EventAction>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        listEvents: async options => {
          set(() => ({ loadingEvents: true }));
          let data: EventList;
          try {
            data = await eventService.list(options);
          } catch (error) {
            set(() => ({
              serverError: parseAxiosError(error),
              loadingEvents: false,
            }));
            throw error;
          }
          set(() => ({
            events: data.items.map((event, index) => ({
              ...event,
              id: index + 1,
              eventId: event.id,
            })),
            nextPage: data.nextPage,
            prevPage: data.prevPage,
            currentPage: Number(data.page),
            totalPages: Number(data.totalPages),
            count: data.itemCount,
            totalCount: data.totalItems,
            loadingEvents: false,
            serverError: '',
          }));
          return data;
        },
        getCodes: async () => {
          try {
            const { data } = await axiosInstance({
              method: 'GET',
              url: `/biomarkers/codes`,
            });
            set({ biomarkers: data });
          } catch (err) {
            console.log(err);
          }
        },
        createEvent: async body => {
          try {
            console.log('body', body);
            const { data } = await axiosInstance({
              method: 'POST',
              url: `/events`,
              data: body,
            });
            return data.id;
          } catch (err) {
            console.log(err);
            throw err;
          }
        },
        deleteEvent: async ({ id }) => {
          set({ deleteEventStatus: { isLoading: true } });
          try {
            await axiosInstance({
              method: 'DELETE',
              url: `/events/${id}`,
            });
            set({
              deleteEventStatus: { isLoading: false },
            });
          } catch (err) {
            set({
              deleteEventStatus: { isLoading: false, error: errorMessage(err) },
            });
          }
        },
        clearCurrentEvent: () => set(() => ({ currentEvent: null })),
        getDetailedEvent: async id => {
          const event = await eventService.getById({ id });
          set(() => ({ currentEvent: event }));
          return event;
        },
        updateEvent: async (id, event) => {
          try {
            console.log('body', event);
            const { data } = await axiosInstance({
              method: 'PATCH',
              url: `/events/${id}`,
              data: event,
            });
          } catch (err) {
            console.log(err);
            throw err;
          }
        },
        downloadCsv: async eventId => {
          await eventService.downloadCsv(eventId || -1);
        },
      }),
      { name: 'event-storage' },
    ),
  ),
);
